import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { AssetGroup } from '@play-co/timestep-core';
import {
  FixedBPointsReward,
  FixedGemsReward,
  Match2Reward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { Match2RewardsPanel } from '../minigame/match2-timestep/components/Match2RewardsPanel';
import {
  animate,
  ImageScaleViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { locale } from 'src/StateObserver';
import { Locale } from 'src/replicant/ruleset/locale';
import { downscale, DOWNSCALE_FACTOR } from 'src/conf/scale';
import {
  FlexBoxImageScaleView,
  FlexBoxImageView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { animatePuzzleRewards } from '../animation/rewardAnimation';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import {
  setCompensation,
  setDisplayFrozen,
} from 'src/game/inventory/inventory.redux';

/**
 * Popup for purchase complete celebration
 *
 *  @param rewards - Match2Reward[] to be shown in the popup, should contains gems
 *  @param skipShowAnimation - Skip show animation
 *  @param skipCloseAnimation- Skip rewards flying animations after popup close
 *
 * @example
 *
 * await openPopupPromise('popupNewPurchaseComplete', {
 *   rewards: [...rewards, { type: 'gems', amount: paidGems + freeGems }],
 *   skipShowAnimation: false,
 *   skipCloseAnimation: false,
 * });
 */
export default class PopupNewPurchaseComplete extends PopupBasicLazy {
  /**
   * Popup content container
   */
  private content!: FlexBoxView;

  /**
   * Set main reward
   */
  private setMainReward: (rewards: Match2Reward[]) => void = () => {};

  /**
   * Set bonus rewards
   */
  private setBonusRewards: (rewards: Match2Reward[]) => void = () => {};

  /**
   * Animations cache
   */
  private animations!: {
    confetti: SpineView;
    radial: SpineView;
    cat: SpineView;
    tiger: SpineView;
  };

  /**
   * Full rewards(Bonus rewards + gems), we use it for finale animation
   */
  private rewards!: Match2Reward[];

  /**
   * Animations layer, where we show all animations, it behind box
   */
  private animationsLayer!: View;

  /**
   * Path to the animation assets
   */
  private completeAnimationAssets =
    'assets/ui/purchaseComplete/animations/spine.purchasecomplete';

  /**
   * Should we skip show and after show animations
   * We use this when purchase happened in the feature UI and we don't wan to complicate or brake feature flow
   */
  private skipCloseAnimation = false;
  private skipShowAnimation = false;

  /**
   * Path to the title assets
   */
  private titleAsset(lang: Locale): {
    image: string;
    width: number;
    height: number;
  } {
    switch (lang) {
      case 'ja':
        return {
          image: 'assets/ui/purchaseComplete/titles/title_ja.png',
          width: downscale(343),
          height: downscale(81),
        };
      default:
        return {
          image: 'assets/ui/purchaseComplete/titles/title_en.png',
          width: downscale(651),
          height: downscale(86),
        };
    }
  }

  /**
   * Load assets on demand
   */
  protected getAssetsGroup(): AssetGroup {
    const assets = [
      this.titleAsset(locale()).image,
      ...AssetGroup.constructURLs(['assets/ui/purchaseComplete/assets/']),
    ];

    if (this.skipShowAnimation) return new AssetGroup(assets);

    return new AssetGroup([
      ...assets,
      { loadMethod: spineLoader, url: this.completeAnimationAssets },
    ]);
  }

  /**
   * Build UI
   */
  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      zIndex: 10001,
      close: () => opts.close(),
      width: downscale(980),
      height: downscale(1340),
      skipTitle: true,
      boxOpts: {
        image: 'assets/ui/purchaseComplete/assets/popup.png',
        scaleMethod: '9slice',
        imgScale: DOWNSCALE_FACTOR,
        sourceSlices: {
          vertical: { top: 150, bottom: 150 },
          horizontal: { left: 150, right: 150 },
        },
      },
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_PURCHASE_COMPLETE._,
      },
    });

    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();

    this.setMainReward = this.createMainRewardChest();
    this.setBonusRewards = this.createBonusRewardsPanel();

    this.createOkButton();
  }

  /**
   * Check if rewards has bonus rewards
   */
  private hasBonusRewards(rewards: Match2Reward[]): boolean {
    return rewards.some((r) => r.type !== 'gems');
  }

  /**
   * If we have gems reward we show gems as main reward and bPoints as bonus rewards.
   * But if we have only bPoints we show them as main reward, but in this case we need to exclude coins from bonus rewards
   */
  private hasOnlyCoinsReward(rewards: Match2Reward[]): boolean {
    return (
      rewards.some((r) => r.type === 'bPoints') &&
      rewards.some((r) => r.type !== 'gems')
    );
  }

  /**
   * Change popup height
   */
  private changePopupHeight(height: number) {
    this.box.updateOpts({
      height: height,
      y: this.root.style.height * 0.5,
      centerOnOrigin: true,
    });
  }

  /**
   * Create custom box
   */
  protected createBox(opts: {
    skipBackground?: boolean;
    boxOpts?: ImageScaleViewOpts;
    width?: number;
    height?: number;
  }): View {
    // Animations layer
    this.animationsLayer = new View({
      superview: this.root,
      width: this.root.style.width,
      height: this.root.style.height,
      infinite: true,
      centerAnchor: true,
    });

    // Box container
    const box = new FlexBoxView({
      superview: this.root,
      infinite: true,
      canHandleEvents: false,
      width: opts.width,
      height: downscale(500),
      x: this.root.style.width * 0.5,
      y: this.root.style.height * 0.5,
      centerAnchor: true,
      centerOnOrigin: true,
      flex: {
        minHeight: downscale(850),
        maxHeight: downscale(1340),
        minWidth: opts.width,
        maxWidth: opts.width,
        flex: 1,
      },
    });

    // Background
    new FlexBoxImageScaleView({
      superview: box,
      ...opts.boxOpts,
      flex: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
    });

    // Title ribbon image
    const header = new FlexBoxImageView({
      superview: box,
      image: 'assets/ui/purchaseComplete/assets/header.png',
      height: downscale(221),
      width: downscale(1050),
      flex: {
        position: 'absolute',
        top: -50,
        left: -(downscale(1050) - box.style.width) * 0.5,
        paddingTop: 15,
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'baseline',
      },
    });

    new FlexBoxImageView({
      superview: header,
      ...this.titleAsset(locale()),
    });

    // Content container
    this.content = new FlexBoxView({
      superview: box,
      flex: {
        position: 'relative',
        padding: 55,
        flexGrow: 1,
        flexShrink: 0,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        alignContent: 'center',
        minHeight: opts.height,
        minWidth: '100%',
      },
    });

    return box;
  }

  /**
   * Crete chest with gems
   */
  private createMainRewardChest() {
    const chest = new ImageView({
      superview: this.content,
      image: 'assets/ui/purchaseComplete/assets/chest_gems.png',
      width: downscale(676),
      height: downscale(400),
    });

    const rewardAmountText = new LangBitmapFontTextView({
      superview: chest,
      font: bitmapFonts('TitleBig'),
      color: '#ffffff',
      localeText: () => 'x0',
      size: 90,
      width: 305,
      height: 144,
      centerOnOrigin: true,
      x: chest.style.width * 0.5,
      y: chest.style.height * 0.5 + 50,
      align: 'center',
      verticalAlign: 'bottom',
    });

    return (rewards: Match2Reward[]) => {
      const gems = rewards.find((r) => r.type === 'gems') as FixedGemsReward;
      const coins = rewards.find(
        (r) => r.type === 'bPoints',
      ) as FixedBPointsReward;

      if (gems) {
        chest.setImage('assets/ui/purchaseComplete/assets/chest_gems.png');
        rewardAmountText.localeText = () => `x${gems.amount}`;
      } else if (coins) {
        // TODO: Use coins chest image
        chest.setImage('assets/ui/purchaseComplete/assets/chest_bpoints.png');
        rewardAmountText.localeText = () => `x${coins.amount}`;
      } else {
        rewardAmountText.localeText = () => `x0`;
      }
    };
  }

  /**
   * Create bonus rewards panel, it can be optional, in case there is no rewards
   */
  private createBonusRewardsPanel() {
    const layout = new FlexBoxView({
      superview: this.content,
      width: 500,
      height: 400,
      flex: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    new LangBitmapFontTextView({
      superview: layout,
      font: bitmapFonts('Body'),
      localeText: () => i18n('purchaseComplete.message'),
      color: '#3F9207',
      size: 38,
      width: 400,
      height: 120,
      align: 'center',
      verticalAlign: 'bottom',
    });

    const panel = new FlexBoxImageScaleView({
      superview: layout,
      image: 'assets/ui/purchaseComplete/assets/bonus_rewards_frame.png',
      width: downscale(727),
      height: downscale(310),
      scaleMethod: '9slice',
      imgScale: DOWNSCALE_FACTOR,
      sourceSlices: {
        vertical: { top: 100, bottom: 100 },
        horizontal: { left: 100, right: 100 },
      },
      flex: {
        marginTop: 30,
      },
    });

    const rewardsPanel = new Match2RewardsPanel({
      superview: panel,
      centerOnOrigin: false,
      width: downscale(720),
      height: downscale(305),
      scale: 0.8,
      itemSize: 100,
      itemPadding: 10,
      itemOffsetX: 10,
      itemOffsetY: 10,
      itemLabelOffsetX: -10,
      itemLabelOffsetY: -10,
      noBg: true,
    });

    return (rewards: Match2Reward[]) => {
      let bonusRewards: Match2Reward[] = rewards.filter(
        (r) => r.type !== 'gems',
      );
      if (this.hasOnlyCoinsReward(rewards)) {
        bonusRewards = rewards.filter((r) => r.type !== 'bPoints');
      }

      const hasBonusRewards = this.hasBonusRewards(rewards);

      if (hasBonusRewards) {
        layout.show();
        this.changePopupHeight(downscale(1340));
        rewardsPanel.setRewards(bonusRewards);
      } else {
        layout.hide();
        this.changePopupHeight(downscale(850));
      }
    };
  }

  /**
   * Create OK button
   */
  private createOkButton() {
    const buttonLayout = new FlexBoxView({
      superview: this.content,
      width: 500,
      height: 200,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: buttonLayout,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      width: downscale(523),
      height: downscale(136),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.opts.close();
      },
      localeText: () => i18n('basic.ok'),
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_PURCHASE_COMPLETE.OK,
      },
    });
  }

  /**
   * Play show popup animation
   */
  private async playShowAnimation() {
    // Lazy create animations
    // We can do in lazy way because without loaded data it won't work
    if (!this.animations) {
      // Animations options
      const animoOpts = {
        superview: this.animationsLayer,
        spinePath: this.completeAnimationAssets,
        x: this.root.style.width * 0.5,
        y: this.root.style.height * 0.5,
        scale: DOWNSCALE_FACTOR,
      };
      this.animations = {
        confetti: new SpineView({ ...animoOpts }),
        radial: new SpineView({ ...animoOpts }),
        cat: new SpineView({ ...animoOpts }),
        tiger: new SpineView({ ...animoOpts }),
      };
    }

    const { confetti, radial, cat, tiger } = this.animations;

    // Change target position depends on if we have bonus rewards
    const hasBonusRewards = this.hasBonusRewards(this.rewards);
    const radialOffsetY = hasBonusRewards ? -300 : -125;
    const petsOffsetY = hasBonusRewards ? -90 : 80;
    const confettiOffsetY = 50;

    // Adjust animals position and confetti
    cat.updateOpts({ offsetY: petsOffsetY });
    tiger.updateOpts({ offsetY: petsOffsetY });
    confetti.updateOpts({ offsetY: confettiOffsetY });

    // Cleanup and prepare animations
    radial.state.clearTracks();
    radial.updateOpts({ offsetY: 0, scale: 0 });
    radial.hide();

    await waitForItPromise(animDuration * 1.5);

    cat.skeleton.setSkinByName('cat');
    cat.state.addAnimation(0, 'purchasecomplete_cat_start');
    cat.state.addAnimation(0, 'purchasecomplete_cat_loop', true);

    tiger.skeleton.setSkinByName('tiger');
    tiger.state.addAnimation(0, 'purchasecomplete_tiger_start');
    tiger.state.addAnimation(0, 'purchasecomplete_tiger_loop', true);

    // Confetti animation
    confetti.state.addAnimation(0, 'confetti');
    animate(confetti)
      .now({ opacity: 1 })
      .wait(animDuration * 9)
      .then({ opacity: 0 }, animDuration * 2, animate.easeOut);

    // Play sounds
    sounds.sfx.play('applause');

    // Show animals on top of popup radial
    animate(radial)
      .then(() => {
        radial.show();
        radial.state.addAnimation(0, 'radial', true);
      })
      .then(
        { offsetY: radialOffsetY, scale: 1 },
        animDuration * 2,
        animate.easeInOutCubic,
      );
  }

  /**
   * On dialog init
   */
  public init(opts: {
    rewards: Match2Reward[];
    skipShowAnimation?: boolean;
    skipCloseAnimation?: boolean;
  }) {
    this.rewards = opts.rewards;
    this.skipShowAnimation = opts.skipShowAnimation ?? false;
    this.skipCloseAnimation = opts.skipCloseAnimation ?? false;

    // Start show animation
    super.init({});

    this.setMainReward(opts.rewards);
    this.setBonusRewards(opts.rewards);
  }

  /**
   * Opn popup show animation
   */
  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();

    this.bg.updateOpts({ opacity: 0 });
    animate(this.bg)
      .clear()
      .then({ opacity: 1 }, animDuration, animate.easeOut);

    // Wait for assets and animations loaded
    await this.loadAssets();

    // Play pets animation
    if (!this.skipShowAnimation) {
      this.animationsLayer.updateOpts({ scale: 1 });
      void this.playShowAnimation();
    }

    // Show popup animations
    sounds.sfx.play('tada');

    // Show the popup
    this.box.show();
    this.box.updateOpts({ scale: 0 });
    animate(this.box)
      .clear()
      .wait(animDuration)
      .then({ scale: 1 }, animDuration, animate.easeOut)
      .then(() => this.overlay.hide());
  }

  /**
   * Close popup animation
   */
  public fadeOut() {
    this.overlay.show();

    // Freeze the header display so it doesn't react to gem state changes
    // during the close animation. fulfillPendingPayment adds gems to
    // inventory after the popup's onSuccess callback, which would cause the
    // header to briefly show the uncompensated value before compensation
    // is applied. Freezing prevents any flicker.
    if (!this.skipCloseAnimation) {
      StateObserver.dispatch(setDisplayFrozen(true));
    }

    animate(this.bg)
      .clear()
      .wait(animDuration)
      .then(() => {
        if (this.skipCloseAnimation) {
          return;
        }

        // Set compensation and unfreeze. By now (~200ms after fadeOut start),
        // fulfillPendingPayment has completed and gems are in state.
        // The instant+unfreeze causes the header to snap to the compensated
        // value (pre-purchase amount) without any animation.
        const gemsAmount = this.rewards
          .filter((r) => r.type === 'gems')
          .reduce((sum, r) => sum + (r as FixedGemsReward).amount, 0);
        if (gemsAmount) {
          StateObserver.dispatch(setCompensation({ gems: gemsAmount }));
        }
        StateObserver.dispatch(setDisplayFrozen(false));
      })
      .then({ opacity: 0 }, animDuration, animate.easeOut)
      .then(async () => {
        this.overlay.hide();
        this.detachRoot();
      })
      .then(async () => {
        if (this.skipCloseAnimation) {
          return;
        }

        await animatePuzzleRewards({
          rewards: this.rewards,
          boostersAndPowerUps: true,
          compensate: true,
        });
      });

    animate(this.animationsLayer)
      .clear()
      .wait(0)
      .then({ scale: 0 }, animDuration, animate.easeOut);

    animate(this.box)
      .clear()
      .wait(0)
      .then({ scale: 0 }, animDuration, animate.easeOut);
  }
}
